import type { NotificationPage } from '@oasis/contracts';
import * as Popover from '@radix-ui/react-popover';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, CheckCheck, Trash2, X } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router';

import { useApi, useApiQuery } from '../api.js';
import { Button, ErrorMessage, Loading } from '../design/components.js';

type Notification = NotificationPage['items'][number];

/** Relève des notifications : chaque minute et au retour sur l'onglet (research S5). */
const POLL_MS = 60_000;

const when = (iso: string): string =>
  new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });

/**
 * Centre de notifications de l'en-tête (US5) : cloche avec le nombre de non lues, dernières
 * notifications, lecture et accusé de traitement, croix pour effacer et « Tout effacer » (celles
 * qui attendent un accusé de traitement restent). Pas de connexion permanente : relève
 * périodique (les alertes ne sont pas du temps réel).
 */
export function NotificationCenter(): ReactElement {
  const [open, setOpen] = useState(false);
  const page = useApiQuery<NotificationPage>('/me/notifications', POLL_MS);
  const call = useApi();
  const client = useQueryClient();
  const refresh = (): Promise<void> => client.invalidateQueries();
  const read = useMutation({
    mutationFn: (id: string) => call<undefined>(`/me/notifications/${id}/read`, { method: 'POST' }),
    onSuccess: refresh,
  });
  const ack = useMutation({
    mutationFn: async (n: Notification) => {
      await call<undefined>(`/me/alerts/${n.alertId ?? ''}/ack`, { method: 'POST' });
      if (n.readAt === null || n.readAt === undefined)
        await call<undefined>(`/me/notifications/${n.id}/read`, { method: 'POST' });
    },
    onSuccess: refresh,
  });
  const dismiss = useMutation({
    mutationFn: (id: string) =>
      call<undefined>(`/me/notifications/${id}/dismiss`, { method: 'POST' }),
    onSuccess: refresh,
  });
  const dismissAll = useMutation({
    mutationFn: () => call<unknown>('/me/notifications/dismiss-all', { method: 'POST' }),
    onSuccess: refresh,
  });
  const error = read.error ?? ack.error ?? dismiss.error ?? dismissAll.error;
  const unread = page.data?.unreadCount ?? 0;
  const label =
    unread === 0
      ? 'Notifications : aucune non lue'
      : `Notifications : ${String(unread)} non lue${unread > 1 ? 's' : ''}`;

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="o-button o-button--ghost o-icon-button o-notifications__trigger"
          aria-label={label}
        >
          <Bell aria-hidden="true" size={20} />
          {unread > 0 && (
            <span className="o-notifications__count" aria-hidden="true">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className="o-menu o-notifications" align="end" sideOffset={6}>
          <div className="o-notifications__head">
            <h2 className="o-notifications__title o-notifications__name">Notifications</h2>
            {page.isSuccess && page.data.items.length > 0 && (
              <Button
                small
                variant="ghost"
                disabled={dismissAll.isPending}
                onClick={() => {
                  dismissAll.mutate();
                }}
              >
                <Trash2 aria-hidden="true" size={16} /> Tout effacer
              </Button>
            )}
          </div>
          {page.isPending && <Loading />}
          {page.isError && <ErrorMessage error={page.error} />}
          {error !== null && <ErrorMessage error={error} />}
          {page.isSuccess && page.data.items.length === 0 && (
            <p className="o-hint">Aucune notification.</p>
          )}
          {page.isSuccess && page.data.items.length > 0 && (
            <ul className="o-list o-notifications__list" aria-live="polite">
              {page.data.items.slice(0, 8).map((n) => {
                const isUnread = n.readAt === null || n.readAt === undefined;
                // Une notification à traiter ne s'efface qu'une fois traitée (règle du serveur).
                const aTraiter =
                  n.ackRequired === true &&
                  n.alertId !== null &&
                  n.alertId !== undefined &&
                  n.acked !== true;
                return (
                  <li
                    key={n.id}
                    className={
                      isUnread
                        ? 'o-notifications__item o-notifications__item--unread'
                        : 'o-notifications__item'
                    }
                  >
                    {/* Titre, puis « Marquer lue » et la croix à côté (retour du référent rc.12). */}
                    <div className="o-notifications__head">
                      {n.link === undefined ? (
                        <strong className="o-notifications__name">{n.title}</strong>
                      ) : (
                        <Link
                          className="o-notifications__name"
                          to={n.link}
                          onClick={() => {
                            if (isUnread) read.mutate(n.id);
                            setOpen(false);
                          }}
                        >
                          <strong>{n.title}</strong>
                        </Link>
                      )}
                      {isUnread && (
                        <Button
                          small
                          variant="ghost"
                          className="o-icon-button"
                          aria-label={`Marquer « ${n.title} » comme lue`}
                          title="Marquer lue"
                          disabled={read.isPending}
                          onClick={() => {
                            read.mutate(n.id);
                          }}
                        >
                          <Check aria-hidden="true" size={16} />
                        </Button>
                      )}
                      {!aTraiter && (
                        <Button
                          small
                          variant="ghost"
                          className="o-icon-button"
                          aria-label={`Effacer « ${n.title} »`}
                          title="Effacer"
                          disabled={dismiss.isPending}
                          onClick={() => {
                            dismiss.mutate(n.id);
                          }}
                        >
                          <X aria-hidden="true" size={16} />
                        </Button>
                      )}
                    </div>
                    <span className="o-hint">{when(n.createdAt)}</span>
                    {aTraiter && (
                      <div className="o-row">
                        <Button
                          small
                          disabled={ack.isPending}
                          onClick={() => {
                            ack.mutate(n);
                          }}
                        >
                          <CheckCheck aria-hidden="true" size={16} /> C'est traité
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <Link
            to="/notifications"
            className="o-button o-button--ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Toutes mes notifications et préférences
          </Link>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
